import { Box, Text, Badge, Portal, createListCollection } from '@chakra-ui/react'
import { Select } from '@chakra-ui/react'
import { Calendar } from 'lucide-react'
import { useFiscalYears, useCurrentFiscalYear } from '../hooks'
import type { FiscalYear } from '../types'

interface FiscalYearSelectorProps {
  residenceId: string
  value?: string
  onChange: (fiscalYear: FiscalYear | null) => void
  showStatus?: boolean
}

export function FiscalYearSelector({
  residenceId,
  value,
  onChange,
  showStatus = true,
}: FiscalYearSelectorProps) {
  const { data: fiscalYears, isLoading } = useFiscalYears(residenceId)
  const { data: currentFiscalYear } = useCurrentFiscalYear(residenceId)

  const selectedId = value || currentFiscalYear?.id

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'blue'
      case 'open':
        return 'green'
      case 'closed':
        return 'gray'
      default:
        return 'gray'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Créé'
      case 'open':
        return 'En cours'
      case 'closed':
        return 'Clôturé'
      default:
        return status
    }
  }

  if (isLoading) {
    return (
      <Box>
        <Text fontSize="sm" color="gray.500">
          Chargement...
        </Text>
      </Box>
    )
  }

  if (!fiscalYears || fiscalYears.length === 0) {
    return (
      <Box display="flex" alignItems="center" gap={2} color="orange.500">
        <Calendar size={16} />
        <Text fontSize="sm">Aucun exercice comptable</Text>
      </Box>
    )
  }

  const selectedFiscalYear = fiscalYears.find((fy) => fy.id === selectedId)

  const collection = createListCollection({
    items: fiscalYears.map((fy) => ({
      label: fy.name,
      value: fy.id,
      status: fy.status,
    })),
  })

  const handleValueChange = (details: { value: string[] }) => {
    const selected = fiscalYears.find((fy) => fy.id === details.value[0]) || null
    onChange(selected)
  }

  return (
    <Box display="inline-block">
      <Select.Root
        collection={collection}
        value={selectedId ? [selectedId] : []}
        onValueChange={handleValueChange}
        size="sm"
        positioning={{ sameWidth: false }}
      >
      <Select.Trigger width="auto">
        <Box display="flex" alignItems="center" gap={2}>
          <Calendar size={16} color="var(--chakra-colors-gray-500)" />
          <Box display="flex" alignItems="center" gap={1}>
            <Select.ValueText placeholder="Sélectionner un exercice" />
            {showStatus && selectedFiscalYear && (
              <Badge
                colorPalette={getStatusColor(selectedFiscalYear.status)}
                variant="subtle"
                size="sm"
              >
                {getStatusLabel(selectedFiscalYear.status)}
              </Badge>
            )}
          </Box>
        </Box>
      </Select.Trigger>
      <Portal>
        <Select.Positioner>
          <Select.Content>
            {collection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Badge
                    colorPalette={getStatusColor(item.status)}
                    variant="subtle"
                    size="sm"
                  >
                    {getStatusLabel(item.status)}
                  </Badge>
                </Box>
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
    </Box>
  )
}
